// The in-memory API's template catalog and authoring (DC-03; services/api/src/routes/
// operator-templates.ts, DC-02's operator-template-versions.ts), with the real routes' rules:
// agency users only (anyone else 404); an agency user sees the platform's templates and its own
// agency's, another agency's are 404; writes need the agency's `template_author` relation (403
// on a visible row otherwise); version numbers grow per template; only a draft is edited or
// validated (409 `version_not_draft`); a failed validation is a 200 that leaves the draft with
// `validation_report.reasons`; only a validated version publishes (422 `publish_refused`
// otherwise) and becomes the template's current one; only a published version is deprecated (409
// `conflict`), its replacement another published version of the same template.
//
// Validation here is a stand-in for the DC-02 library: it passes with a verification skill pack,
// at least one eval, a `lead` role and standing orders; an eval whose `spec_ref` contains "fail"
// fails. A write repeated under the same `Idempotency-Key` replays its first answer.
import type { OperationOutput, Problem, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, page, validation } from "./fake";
import { COMPANY_IDS, uid } from "./fake-data";
import type {
  AgentTemplate,
  AgentTemplateVersion,
  MeResponse,
  TemplateEditProposal,
  TemplateEval,
  TemplateRule,
} from "./types";

type Detail = OperationOutput<"operator.templateVersions.get">;

/** HTVA's agency (the demo operator's) and another agency whose catalog HTVA never sees. */
export const FAKE_AGENCY_ID = "0193a0a0-0000-7000-8000-00000000a9e1";
export const OTHER_AGENCY_ID = "0193a0a0-0000-7000-8000-00000000a9e2";

/** The seeded catalog's ids (tests and e2e reach the rows through them). */
export const TEMPLATE_IDS = {
  coordinator: uid(0x7d00, 1),
  basic: uid(0x7d00, 2),
  otherAgency: uid(0x7d00, 3),
  /** DC-05: HTVA's interview scheduler, a draft with no verification skill (pilots only). */
  pilot: uid(0x7d00, 4),
  /**
   * DC-11: the platform's self-serve catalog, with the API seed's fixed ids
   * (services/api/src/domain/deploys/self-serve-catalog.ts).
   */
  empty: "019a0000-0000-7000-8000-000000dc1101",
  research: "019a0000-0000-7000-8000-000000dc1111",
} as const;
export const VERSION_IDS = {
  coordinatorV1: uid(0x7d10, 1),
  coordinatorV2: uid(0x7d10, 2),
  otherAgencyV1: uid(0x7d10, 3),
  pilotV1: uid(0x7d10, 4),
  emptyV1: "019a0000-0000-7000-8000-000000dc1102",
  researchV1: "019a0000-0000-7000-8000-000000dc1112",
} as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;
const ROLE = /^[a-z][a-z0-9_]*$/;
const EVAL_KINDS = ["fixture", "replay", "live"] as const;
const ENFORCERS = ["pipeline", "schema", "tool_block", "eval", "written_rule"] as const;
const SEEDED = "2026-09-01T09:00:00.000Z";
const PUBLISHED = "2026-09-02T09:00:00.000Z";

/** A problem with field errors (the shared helper has none). */
function problemOf(
  status: number,
  code: Problem["code"],
  title: string,
  detail: string,
  errors?: { path: string; code: string; message: string }[],
): Response {
  return new Response(
    JSON.stringify({
      type: `https://companyos.app/errors/${code}`,
      title,
      status,
      code,
      detail,
      request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
      ...(errors ? { errors } : {}),
    }),
    { status, headers: { "content-type": "application/problem+json" } },
  );
}

const forbidden = () =>
  problemOf(
    403,
    "forbidden",
    "Forbidden",
    "authoring the catalog needs the agency's template_author relation",
  );
const notDraft = () =>
  problemOf(409, "version_not_draft", "A validated template version is immutable", "not a draft");

/** A 64-hex digest of `text` (not cryptographic: the fake only needs a stable, well-formed hash). */
function digest(text: string): string {
  let out = "";
  for (let seed = 0; out.length < 64; seed++) {
    let h = 0x811c9dc5 ^ seed;
    for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
    out += (h >>> 0).toString(16).padStart(8, "0");
  }
  return out.slice(0, 64);
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");

function mistakeProblem(v: unknown, path: string): string | null {
  if (!isObj(v)) return `${path}: company_id and run_id are required`;
  if (!UUID.test(str(v.company_id))) return `${path}.company_id: must be a UUID`;
  if (!UUID.test(str(v.run_id))) return `${path}.run_id: must be a UUID`;
  if (v.run_decision_id !== undefined && !UUID.test(str(v.run_decision_id)))
    return `${path}.run_decision_id: must be a UUID`;
  return null;
}

/** The body checks the contract makes (TemplateVersionDraft / TemplateVersionPatch). */
function bodyProblem(b: Record<string, unknown>, partial: boolean): string | null {
  if (!partial || b.standing_orders_text !== undefined) {
    const t = str(b.standing_orders_text).trim();
    if (!t) return "standing_orders_text: required";
    if (t.length > 10_000) return "standing_orders_text: at most 10000 characters";
  }
  if (!partial || b.role_model_map !== undefined) {
    if (!isObj(b.role_model_map)) return "role_model_map: required";
    for (const [role, entry] of Object.entries(b.role_model_map)) {
      if (!ROLE.test(role)) return `role_model_map.${role}: lowercase letters, digits and _`;
      if (!isObj(entry) || !str(entry.model).trim())
        return `role_model_map.${role}.model: required`;
      const paths = entry.paths;
      const ok =
        Array.isArray(paths) &&
        ((paths.length === 1 && (paths[0] === "gateway" || paths[0] === "subscription")) ||
          (paths.length === 2 && paths[0] === "subscription" && paths[1] === "gateway"));
      if (!ok)
        return `role_model_map.${role}.paths: a fallback order is only subscription, gateway`;
    }
  }
  for (const k of ["verification_skill_pack_id", "supersedes_version_id"] as const)
    if (b[k] !== undefined && b[k] !== null && !UUID.test(str(b[k]))) return `${k}: must be a UUID`;
  if (b.skill_index_token_cap !== undefined) {
    const n = b.skill_index_token_cap;
    if (typeof n !== "number" || !Number.isInteger(n) || n < 1 || n > 32_000)
      return "skill_index_token_cap: an integer from 1 to 32000";
  }
  if (b.evals !== undefined) {
    if (!Array.isArray(b.evals)) return "evals: must be a list";
    for (const [i, e] of b.evals.entries()) {
      if (!isObj(e) || !str(e.key).trim()) return `evals.${i}.key: required`;
      if (!EVAL_KINDS.includes(e.kind as never)) return `evals.${i}.kind: fixture, replay or live`;
      if (!str(e.spec_ref).trim()) return `evals.${i}.spec_ref: required`;
      if (e.proves_mistake_ref !== undefined) {
        const bad = mistakeProblem(e.proves_mistake_ref, `evals.${i}.proves_mistake_ref`);
        if (bad) return bad;
      }
    }
  }
  if (b.rules !== undefined) {
    if (!Array.isArray(b.rules)) return "rules: must be a list";
    for (const [i, r] of b.rules.entries()) {
      if (!isObj(r) || !str(r.key).trim()) return `rules.${i}.key: required`;
      if (!str(r.rule_text).trim()) return `rules.${i}.rule_text: required`;
      if (!ENFORCERS.includes(r.enforcer_kind as never))
        return `rules.${i}.enforcer_kind: unknown enforcer`;
      if (!str(r.enforcer_ref).trim()) return `rules.${i}.enforcer_ref: required`;
      const bad = mistakeProblem(r.proven_against_ref, `rules.${i}.proven_against_ref`);
      if (bad) return bad;
      if (r.enforcer_kind === "written_rule" && !str(r.reason_if_written).trim())
        return `rules.${i}.reason_if_written: a written_rule needs reason_if_written (why nothing stronger applies)`;
    }
  }
  return null;
}

/** What other fakes read of the catalog (DC-04's deploys stage from published versions). */
export type FakeCatalog = {
  versionFor(me: MeResponse, id: string | undefined): AgentTemplateVersion | null;
  templateFor(me: MeResponse, id: string | undefined): AgentTemplate | null;
  /** DC-05: a version's template name and number as a company sees them on its deploys. */
  labelOf(versionId: string): { template_name: string; version: number } | null;
  /**
   * AGENT-MENTION: a version's template id, name and own standing orders, as the company's DE
   * profile reads them (the API reads the cp row whoever asks; no agency visibility).
   */
  versionText(
    versionId: string,
  ): { template_id: string; template_name: string; standing_orders_text: string } | null;
  /**
   * DC-11: the platform's self-serve catalog (class `empty` or `basic`, published current
   * version), the empty agent first.
   */
  selfServe(): { template: AgentTemplate; version: AgentTemplateVersion }[];
  /** BILLING-PLANS: every template head (Operator › Billing › Plans lists each one's fee). */
  all(): readonly AgentTemplate[];
};

export function createTemplateHandlers(deps: {
  now: () => Date;
  share?: (catalog: FakeCatalog) => void;
}): Partial<Record<RouteId, Handler>> {
  const at = () => deps.now().toISOString();
  let seq = 0x100;
  const templates: AgentTemplate[] = [];
  const versions: AgentTemplateVersion[] = [];
  const evals: TemplateEval[] = [];
  const rules: TemplateRule[] = [];
  const proposals: TemplateEditProposal[] = [];
  const replays = new Map<string, Response>();

  // Seed ----------------------------------------------------------------------------------------

  const head = (
    t: Pick<AgentTemplate, "id" | "scope" | "agency_id" | "key" | "name" | "template_class"> &
      Partial<AgentTemplate>,
  ): AgentTemplate => ({
    version: 1,
    kind: "persistent",
    status: "draft",
    instructions_ref: null,
    skill_pack_ids: [],
    tool_grants: {},
    model_policy: {},
    runtime_config: {},
    license_note: null,
    published_at: null,
    deprecated_at: null,
    created_by_user_id: null,
    current_version_id: null,
    maintenance_tier: null,
    setup_fee_minor: null,
    created_at: SEEDED,
    updated_at: SEEDED,
    ...t,
  });
  const version = (
    v: Pick<AgentTemplateVersion, "id" | "template_id" | "version" | "state"> &
      Partial<AgentTemplateVersion>,
  ): AgentTemplateVersion => ({
    source: "author",
    supersedes_version_id: null,
    skill_index_ref: null,
    skill_index_tokens: null,
    skill_index_token_cap: 2000,
    verification_skill_pack_id: null,
    role_model_map: {},
    standing_orders_text: "",
    tool_grants: {},
    runtime_config: {},
    reporting_defaults: {},
    content_hash: null,
    validation_report: null,
    validated_at: null,
    published_at: null,
    deprecated_at: null,
    created_by_user_id: null,
    changelog: null,
    // TEMPLATES-T2: a role template's defaults (none on the fake's versions).
    required_connections: null,
    access_defaults: null,
    kpis: null,
    cadence: null,
    model_tier: null,
    budget_default_minor: null,
    probation: null,
    created_at: SEEDED,
    updated_at: SEEDED,
    ...v,
  });
  const mistake = (n: number) => ({ company_id: COMPANY_IDS.acme, run_id: uid(0x7d40, n) });
  const addEval = (versionId: string, e: Partial<TemplateEval> & Pick<TemplateEval, "key">) => {
    evals.push({
      id: uid(0x7d20, ++seq),
      template_version_id: versionId,
      kind: "fixture",
      spec_ref: `templates/${e.key}.json`,
      proves_mistake_ref: null,
      fails_on_version_id: null,
      passes_on_version_id: null,
      last_result: null,
      last_run_at: null,
      retired_at: null,
      created_at: SEEDED,
      ...e,
    });
  };
  const addRule = (versionId: string, r: Partial<TemplateRule> & Pick<TemplateRule, "key">) => {
    rules.push({
      id: uid(0x7d30, ++seq),
      template_version_id: versionId,
      rule_text: "",
      enforcer_kind: "pipeline",
      enforcer_ref: "pipeline:default",
      proven_against_ref: mistake(1),
      proven_at: null,
      reason_if_written: null,
      created_at: SEEDED,
      ...r,
    });
  };

  const ORDERS =
    "You coordinate interviews for the recruiting desk. Confirm every slot with the candidate " +
    "and the hiring manager before it goes on the recruiting board.";
  const MAP = {
    lead: { model: "gpt-4o", paths: ["gateway"] },
    review: { model: "claude-sonnet", paths: ["subscription", "gateway"] },
  } satisfies AgentTemplateVersion["role_model_map"];
  templates.push(
    head({
      id: TEMPLATE_IDS.coordinator,
      scope: "platform",
      agency_id: null,
      key: "recruiting-coordinator",
      name: "Recruiting coordinator",
      template_class: "managed",
      status: "published",
      published_at: PUBLISHED,
      current_version_id: VERSION_IDS.coordinatorV1,
    }),
    head({
      id: TEMPLATE_IDS.basic,
      scope: "platform",
      agency_id: null,
      key: "basic-assistant",
      name: "Basic assistant",
      kind: "ephemeral",
      template_class: "basic",
    }),
    head({
      id: TEMPLATE_IDS.otherAgency,
      scope: "agency",
      agency_id: OTHER_AGENCY_ID,
      key: "other-agency-sourcer",
      name: "Other agency sourcer",
      template_class: "managed",
      status: "published",
      published_at: PUBLISHED,
      current_version_id: VERSION_IDS.otherAgencyV1,
    }),
    // DC-05: no verification skill yet, so it never leaves draft and deploys only as a pilot.
    head({
      id: TEMPLATE_IDS.pilot,
      scope: "agency",
      agency_id: FAKE_AGENCY_ID,
      key: "interview-scheduler",
      name: "Interview scheduler",
      template_class: "managed",
    }),
    // DC-11: the platform's self-serve catalog, as the API seed writes it.
    head({
      id: TEMPLATE_IDS.empty,
      scope: "platform",
      agency_id: null,
      key: "empty",
      name: "Empty agent",
      template_class: "empty",
      status: "published",
      published_at: PUBLISHED,
      current_version_id: VERSION_IDS.emptyV1,
    }),
    head({
      id: TEMPLATE_IDS.research,
      scope: "platform",
      agency_id: null,
      key: "basic-research-assistant",
      name: "Research assistant",
      template_class: "basic",
      status: "published",
      published_at: PUBLISHED,
      current_version_id: VERSION_IDS.researchV1,
    }),
  );
  const SELF_SERVE_MAP = {
    lead: { model: "claude-sonnet", paths: ["gateway"] },
  } satisfies AgentTemplateVersion["role_model_map"];
  const EMPTY_ORDERS = [
    "Work only on what your supervisor or a teammate asks of you in CompanyOS.",
    "Ask your supervisor before anything that spends money, contacts someone outside the company, or cannot be undone.",
    "Say plainly when you are unsure or could not finish, and what you would need.",
  ].join("\n");
  const RESEARCH_ORDERS = [
    "Research what a teammate asks and answer in the thread it was asked in.",
    "Cite every source you used, with a link, next to the claim it supports.",
    "Ask your supervisor before anything that spends money, contacts someone outside the company, or cannot be undone.",
  ].join("\n");
  versions.push(
    version({
      id: VERSION_IDS.emptyV1,
      template_id: TEMPLATE_IDS.empty,
      version: 1,
      state: "published",
      skill_index_tokens: 0,
      role_model_map: SELF_SERVE_MAP,
      standing_orders_text: EMPTY_ORDERS,
      content_hash: digest(`empty${EMPTY_ORDERS}`),
      validation_report: { reasons: [], deployable: true, verificationState: "unverified" },
      validated_at: PUBLISHED,
      published_at: PUBLISHED,
      changelog: "Platform self-serve catalog (DC-11)",
    }),
    version({
      id: VERSION_IDS.researchV1,
      template_id: TEMPLATE_IDS.research,
      version: 1,
      state: "published",
      skill_index_ref: "templates/platform/basic-research-assistant/v1/skill-index.json",
      skill_index_tokens: 46,
      verification_skill_pack_id: "019a0000-0000-7000-8000-000000dc1119",
      role_model_map: SELF_SERVE_MAP,
      standing_orders_text: RESEARCH_ORDERS,
      content_hash: digest(`research${RESEARCH_ORDERS}`),
      validation_report: { reasons: [], deployable: true, verificationState: "verified" },
      validated_at: PUBLISHED,
      published_at: PUBLISHED,
      changelog: "Platform self-serve catalog (DC-11)",
    }),
  );
  const v1 = version({
    id: VERSION_IDS.coordinatorV1,
    template_id: TEMPLATE_IDS.coordinator,
    version: 1,
    state: "published",
    skill_index_ref: "templates/recruiting-coordinator/v1/skill-index.json",
    skill_index_tokens: 840,
    verification_skill_pack_id: uid(0x7d50, 1),
    role_model_map: MAP,
    standing_orders_text: ORDERS,
    content_hash: digest(`v1${ORDERS}`),
    validation_report: { reasons: [], deployable: true },
    validated_at: PUBLISHED,
    published_at: PUBLISHED,
    changelog: "First catalog version.",
  });
  const v2 = version({
    id: VERSION_IDS.coordinatorV2,
    template_id: TEMPLATE_IDS.coordinator,
    version: 2,
    state: "draft",
    supersedes_version_id: v1.id,
    skill_index_ref: "templates/recruiting-coordinator/v2/skill-index.json",
    verification_skill_pack_id: uid(0x7d50, 1),
    role_model_map: MAP,
    standing_orders_text: `${ORDERS} Never book a slot outside the candidate's stated hours.`,
    changelog: "Respect the candidate's stated hours.",
  });
  versions.push(
    v1,
    v2,
    version({
      id: VERSION_IDS.otherAgencyV1,
      template_id: TEMPLATE_IDS.otherAgency,
      version: 1,
      state: "published",
      verification_skill_pack_id: uid(0x7d50, 2),
      role_model_map: { lead: { model: "gpt-4o", paths: ["gateway"] } },
      standing_orders_text: "Source candidates for the other agency.",
      content_hash: digest("other"),
      validation_report: { reasons: [] },
      validated_at: PUBLISHED,
      published_at: PUBLISHED,
    }),
    version({
      id: VERSION_IDS.pilotV1,
      template_id: TEMPLATE_IDS.pilot,
      version: 1,
      state: "draft",
      skill_index_ref: "templates/interview-scheduler/v1/skill-index.json",
      role_model_map: { lead: { model: "gpt-4o", paths: ["gateway"] } },
      standing_orders_text:
        "Book interviews only inside the hiring manager's published hours. Never email candidates directly.",
      changelog: "Pilot: no verification skill yet.",
    }),
  );
  addEval(v1.id, {
    key: "confirms-slots",
    spec_ref: "templates/recruiting-coordinator/evals/confirms-slots.json",
    last_result: "pass",
    last_run_at: PUBLISHED,
  });
  addEval(v1.id, {
    key: "no-double-booking",
    kind: "replay",
    spec_ref: "templates/recruiting-coordinator/evals/no-double-booking.json",
    proves_mistake_ref: mistake(2),
    last_result: "pass",
    last_run_at: PUBLISHED,
  });
  addRule(v1.id, {
    key: "board-confirmed-only",
    rule_text: "Only confirmed slots go on the recruiting board.",
    enforcer_kind: "tool_block",
    enforcer_ref: "tools/board.write:confirmed",
  });
  addRule(v1.id, {
    key: "tone",
    rule_text: "Write to candidates in plain, friendly language.",
    enforcer_kind: "written_rule",
    enforcer_ref: "standing_orders",
    reason_if_written: "Tone has no mechanical check yet.",
  });
  addEval(v2.id, {
    key: "stated-hours",
    spec_ref: "templates/recruiting-coordinator/evals/stated-hours.json",
    proves_mistake_ref: mistake(3),
  });
  addRule(v2.id, {
    key: "board-confirmed-only",
    rule_text: "Only confirmed slots go on the recruiting board.",
    enforcer_kind: "tool_block",
    enforcer_ref: "tools/board.write:confirmed",
  });
  const firstEval = evals[0];
  if (firstEval)
    proposals.push({
      id: uid(0x7d60, 1),
      template_id: TEMPLATE_IDS.coordinator,
      based_on_version_id: v1.id,
      source: "reflection",
      run_refs: [mistake(3)],
      diff_ref: "templates/recruiting-coordinator/proposals/stated-hours.diff",
      eval_id: firstEval.id,
      state: "proposed",
      accepted_version_id: null,
      proposed_by: { kind: "run", ...mistake(4) },
      decided_by_user_id: null,
      decided_at: null,
      reason: null,
      created_at: PUBLISHED,
    });

  // Access --------------------------------------------------------------------------------------

  const visible = (me: MeResponse, t: AgentTemplate) =>
    t.scope === "platform" || (me.agency !== null && t.agency_id === me.agency.agency_id);
  const templateFor = (me: MeResponse, id: string | undefined) => {
    const t = templates.find((x) => x.id === id?.toLowerCase());
    return t && visible(me, t) ? t : null;
  };
  const versionFor = (me: MeResponse, id: string | undefined) => {
    const v = versions.find((x) => x.id === id?.toLowerCase());
    return v && templateFor(me, v.template_id) ? v : null;
  };
  const labelOf = (versionId: string) => {
    const v = versions.find((x) => x.id === versionId);
    const t = v && templates.find((x) => x.id === v.template_id);
    return v && t ? { template_name: t.name, version: v.version } : null;
  };
  const selfServe = () =>
    templates
      .filter(
        (t) =>
          t.scope === "platform" &&
          (t.template_class === "empty" || t.template_class === "basic") &&
          t.current_version_id !== null,
      )
      .flatMap((t) => {
        const v = versions.find((x) => x.id === t.current_version_id && x.state === "published");
        return v ? [{ template: t, version: v }] : [];
      })
      .sort((a, b) =>
        a.template.template_class === b.template.template_class
          ? a.template.name.localeCompare(b.template.name)
          : a.template.template_class === "empty"
            ? -1
            : 1,
      );
  const versionText = (versionId: string) => {
    const v = versions.find((x) => x.id === versionId);
    const t = v && templates.find((x) => x.id === v.template_id);
    return v && t
      ? { template_id: t.id, template_name: t.name, standing_orders_text: v.standing_orders_text }
      : null;
  };
  deps.share?.({ versionFor, templateFor, labelOf, versionText, selfServe, all: () => templates });
  const author = (me: MeResponse) => me.agency?.template_author === true;
  const detailOf = (v: AgentTemplateVersion): Detail => ({
    version: v,
    evals: evals.filter((e) => e.template_version_id === v.id),
    rules: rules.filter((r) => r.template_version_id === v.id),
  });

  /** Replays a write's first answer under the same Idempotency-Key. */
  const once =
    (id: RouteId, handler: Handler): Handler =>
    (ctx: Ctx) => {
      const key = ctx.headers.get("idempotency-key");
      const k = key ? `${ctx.me.user.id}:${id}:${key}` : null;
      const hit = k ? replays.get(k) : undefined;
      if (hit) return hit.clone();
      const res = handler(ctx);
      if (k && res.status < 500) replays.set(k, res.clone());
      return res;
    };

  function setEvals(versionId: string, list: unknown[]) {
    for (let i = evals.length - 1; i >= 0; i--)
      if (evals[i]?.template_version_id === versionId) evals.splice(i, 1);
    for (const e of list as Record<string, unknown>[])
      addEval(versionId, {
        key: str(e.key).trim(),
        kind: e.kind as TemplateEval["kind"],
        spec_ref: str(e.spec_ref).trim(),
        proves_mistake_ref: isObj(e.proves_mistake_ref)
          ? (e.proves_mistake_ref as TemplateEval["proves_mistake_ref"])
          : null,
        created_at: at(),
      });
  }
  function setRules(versionId: string, list: unknown[]) {
    for (let i = rules.length - 1; i >= 0; i--)
      if (rules[i]?.template_version_id === versionId) rules.splice(i, 1);
    for (const r of list as Record<string, unknown>[])
      addRule(versionId, {
        key: str(r.key).trim(),
        rule_text: str(r.rule_text).trim(),
        enforcer_kind: r.enforcer_kind as TemplateRule["enforcer_kind"],
        enforcer_ref: str(r.enforcer_ref).trim(),
        proven_against_ref: r.proven_against_ref as TemplateRule["proven_against_ref"],
        proven_at: typeof r.proven_at === "string" ? r.proven_at : null,
        reason_if_written: str(r.reason_if_written).trim() || null,
        created_at: at(),
      });
  }

  /** The stand-in for the DC-02 gate: the reasons a draft does not validate. */
  function check(v: AgentTemplateVersion): string[] {
    const reasons: string[] = [];
    const own = evals.filter((e) => e.template_version_id === v.id);
    if (!v.verification_skill_pack_id)
      reasons.push("verification_skill_missing: the version needs a verification skill pack");
    if (own.length === 0) reasons.push("evals_missing: at least one eval must pass");
    if (!v.role_model_map.lead)
      reasons.push("lead_model_missing: the role-to-model map needs a `lead` role");
    if (!v.standing_orders_text.trim()) reasons.push("standing_orders_empty: no standing orders");
    for (const e of own)
      if (e.spec_ref.includes("fail"))
        reasons.push(`eval_failed (${e.key}): the eval did not pass`);
    return reasons;
  }

  // Routes --------------------------------------------------------------------------------------

  return {
    "operator.agentTemplates.list": ({ me, url }) => {
      if (!me.agency) return notFound();
      const cls = url.searchParams.get("template_class");
      const rows = templates
        .filter((t) => visible(me, t) && (!cls || t.template_class === cls))
        .sort((a, b) => a.name.localeCompare(b.name));
      return json(page(rows, url));
    },
    "operator.agentTemplates.create": once("operator.agentTemplates.create", ({ me, body }) => {
      if (!me.agency) return notFound();
      const b = (isObj(body) ? body : {}) as Record<string, unknown>;
      const own = me.agency.agency_id;
      if (b.scope !== "platform" && b.scope !== "agency")
        return validation("scope: platform or agency");
      if (
        b.scope === "agency" &&
        b.agency_id !== undefined &&
        str(b.agency_id).toLowerCase() !== own
      )
        return notFound();
      if (b.scope === "platform" && b.agency_id !== undefined)
        return validation("agency_id: a platform template has no agency");
      if (!SLUG.test(str(b.key)))
        return validation("key: lowercase letters, digits and dashes, 2–63 chars");
      if (!str(b.name).trim()) return validation("name: required");
      if (b.kind !== "persistent" && b.kind !== "ephemeral")
        return validation("kind: persistent or ephemeral");
      const cls = b.template_class ?? "managed";
      if (cls !== "managed" && cls !== "basic" && cls !== "empty")
        return validation("template_class: managed, basic or empty");
      if (b.scope === "agency" && cls !== "managed")
        return validation("template_class: basic and empty templates are platform-owned");
      if (!author(me)) return forbidden();
      if (templates.some((t) => t.key === b.key))
        return problemOf(409, "conflict", "Conflict", `a template ${str(b.key)} already exists`);
      const t = head({
        id: uid(0x7d00, ++seq),
        scope: b.scope,
        agency_id: b.scope === "agency" ? own : null,
        key: str(b.key),
        name: str(b.name).trim(),
        kind: b.kind,
        template_class: cls,
        created_by_user_id: me.user.id,
        created_at: at(),
        updated_at: at(),
      });
      templates.push(t);
      return json(t, 201);
    }),
    "operator.agentTemplates.get": ({ me, params }) => {
      if (!me.agency) return notFound();
      const t = templateFor(me, params.template_id);
      if (!t) return notFound();
      return json({
        template: t,
        versions: versions
          .filter((v) => v.template_id === t.id)
          .sort((a, b) => b.version - a.version),
      });
    },
    "operator.templateVersions.create": once(
      "operator.templateVersions.create",
      ({ me, params, body }) => {
        if (!me.agency) return notFound();
        const t = templateFor(me, params.template_id);
        if (!t) return notFound();
        if (!author(me)) return forbidden();
        const b = (isObj(body) ? body : {}) as Record<string, unknown>;
        const bad = bodyProblem(b, false);
        if (bad) return validation(bad);
        const supersedes = str(b.supersedes_version_id).toLowerCase() || null;
        if (supersedes && !versions.some((v) => v.id === supersedes && v.template_id === t.id))
          return validation("supersedes_version_id: must be a version of this template");
        const n = Math.max(
          0,
          ...versions.filter((v) => v.template_id === t.id).map((v) => v.version),
        );
        const v = version({
          id: uid(0x7d10, ++seq),
          template_id: t.id,
          version: n + 1,
          state: "draft",
          supersedes_version_id: supersedes,
          skill_index_ref: str(b.skill_index_ref).trim() || null,
          skill_index_token_cap: (b.skill_index_token_cap as number | undefined) ?? 2000,
          verification_skill_pack_id: str(b.verification_skill_pack_id).toLowerCase() || null,
          role_model_map: b.role_model_map as AgentTemplateVersion["role_model_map"],
          standing_orders_text: str(b.standing_orders_text).trim(),
          tool_grants: isObj(b.tool_grants) ? b.tool_grants : {},
          runtime_config: isObj(b.runtime_config) ? b.runtime_config : {},
          changelog: str(b.changelog) || null,
          created_by_user_id: me.user.id,
          created_at: at(),
          updated_at: at(),
        });
        versions.push(v);
        setEvals(v.id, Array.isArray(b.evals) ? b.evals : []);
        setRules(v.id, Array.isArray(b.rules) ? b.rules : []);
        return json(detailOf(v), 201);
      },
    ),
    "operator.templateVersions.get": ({ me, params }) => {
      if (!me.agency) return notFound();
      const v = versionFor(me, params.version_id);
      return v ? json(detailOf(v)) : notFound();
    },
    "operator.templateVersions.update": ({ me, params, body }) => {
      if (!me.agency) return notFound();
      const v = versionFor(me, params.version_id);
      if (!v) return notFound();
      if (!author(me)) return forbidden();
      if (v.state !== "draft") return notDraft();
      const b = (isObj(body) ? body : {}) as Record<string, unknown>;
      const bad = bodyProblem(b, true);
      if (bad) return validation(bad);
      if (b.skill_index_ref !== undefined)
        v.skill_index_ref = str(b.skill_index_ref).trim() || null;
      if (b.skill_index_token_cap !== undefined)
        v.skill_index_token_cap = b.skill_index_token_cap as number;
      if (b.verification_skill_pack_id !== undefined)
        v.verification_skill_pack_id = str(b.verification_skill_pack_id).toLowerCase() || null;
      if (b.role_model_map !== undefined)
        v.role_model_map = b.role_model_map as AgentTemplateVersion["role_model_map"];
      if (b.standing_orders_text !== undefined)
        v.standing_orders_text = str(b.standing_orders_text).trim();
      if (isObj(b.tool_grants)) v.tool_grants = b.tool_grants;
      if (isObj(b.runtime_config)) v.runtime_config = b.runtime_config;
      if (b.changelog !== undefined) v.changelog = str(b.changelog) || null;
      if (Array.isArray(b.evals)) setEvals(v.id, b.evals);
      if (Array.isArray(b.rules)) setRules(v.id, b.rules);
      v.updated_at = at();
      return json(detailOf(v));
    },
    "operator.templateVersions.validate": once(
      "operator.templateVersions.validate",
      ({ me, params }) => {
        if (!me.agency) return notFound();
        const v = versionFor(me, params.version_id);
        if (!v) return notFound();
        if (!author(me)) return forbidden();
        if (v.state !== "draft") return notDraft();
        const reasons = check(v);
        const t = at();
        for (const e of evals)
          if (e.template_version_id === v.id) {
            e.last_result = e.spec_ref.includes("fail") ? "fail" : "pass";
            e.last_run_at = t;
          }
        const tokens = 120 + (v.standing_orders_text.length % 400);
        v.validation_report = {
          reasons,
          deployable: reasons.length === 0,
          skillIndexTokens: tokens,
        };
        if (reasons.length === 0) {
          v.state = "validated";
          v.validated_at = t;
          v.skill_index_tokens = tokens;
          v.content_hash = digest(
            `${v.id}${v.standing_orders_text}${JSON.stringify(v.role_model_map)}`,
          );
        }
        v.updated_at = t;
        return json(detailOf(v));
      },
    ),
    "operator.templateVersions.publish": once(
      "operator.templateVersions.publish",
      ({ me, params }) => {
        if (!me.agency) return notFound();
        const v = versionFor(me, params.version_id);
        if (!v) return notFound();
        if (!author(me)) return forbidden();
        const title =
          "Publish needs a validated version, a verification skill and every eval passing";
        if (v.state !== "validated")
          return problemOf(
            422,
            "publish_refused",
            title,
            `a ${v.state} version cannot be published; validate a draft first`,
          );
        const reasons = check(v);
        if (reasons.length)
          return problemOf(
            422,
            "publish_refused",
            title,
            reasons.join("; "),
            reasons.map((message) => ({ path: "", code: message.split(":")[0] ?? "", message })),
          );
        const t = at();
        v.state = "published";
        v.published_at = t;
        v.updated_at = t;
        const tpl = templates.find((x) => x.id === v.template_id);
        if (tpl) {
          tpl.status = "published";
          tpl.current_version_id = v.id;
          tpl.published_at = tpl.published_at ?? t;
          tpl.deprecated_at = null;
          tpl.updated_at = t;
        }
        return json(v);
      },
    ),
    "operator.templateVersions.deprecate": once(
      "operator.templateVersions.deprecate",
      ({ me, params, body }) => {
        if (!me.agency) return notFound();
        const v = versionFor(me, params.version_id);
        if (!v) return notFound();
        if (!author(me)) return forbidden();
        if (v.state !== "published")
          return problemOf(
            409,
            "conflict",
            "Conflict",
            `only a published version can be deprecated; this one is ${v.state}`,
          );
        const b = (isObj(body) ? body : {}) as Record<string, unknown>;
        let replacedBy: string | null = null;
        if (b.replaced_by_version_id !== undefined) {
          const r = versions.find(
            (x) =>
              x.id === str(b.replaced_by_version_id).toLowerCase() &&
              x.template_id === v.template_id &&
              x.state === "published" &&
              x.id !== v.id,
          );
          if (!r)
            return validation(
              "replaced_by_version_id: must be another published version of this template",
            );
          replacedBy = r.id;
        }
        const t = at();
        v.state = "deprecated";
        v.deprecated_at = t;
        v.updated_at = t;
        const tpl = templates.find((x) => x.id === v.template_id);
        if (tpl && tpl.current_version_id === v.id) {
          const current =
            replacedBy ??
            versions
              .filter((x) => x.template_id === v.template_id && x.state === "published")
              .sort((a, b2) => b2.version - a.version)[0]?.id ??
            null;
          tpl.current_version_id = current;
          if (!current) {
            tpl.status = "deprecated";
            tpl.deprecated_at = t;
          }
          tpl.updated_at = t;
        }
        return json(v);
      },
    ),
    "operator.templateEditProposals.list": ({ me, url }) => {
      if (!me.agency) return notFound();
      const tid = url.searchParams.get("template_id");
      const state = url.searchParams.get("state");
      const rows = proposals
        .filter(
          (p) =>
            templateFor(me, p.template_id) &&
            (!tid || p.template_id === tid.toLowerCase()) &&
            (!state || p.state === state),
        )
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      return json(page(rows, url));
    },
  };
}
